// =============================================================================
// BP-05-03: Calendar Frontend - PrepChecklist Component
// =============================================================================

import React, { useState } from 'react';
import { CheckCircle, Circle, Plus, Trash2 } from 'lucide-react';
import { type PrepChecklistItem } from '../types';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

interface PrepChecklistProps {
  items: PrepChecklistItem[];
  onToggle?: (itemId: string, completed: boolean) => void;
  onAdd?: (item: string) => void;
  onRemove?: (itemId: string) => void;
  readOnly?: boolean;
  className?: string;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function PrepChecklist({
  items,
  onToggle,
  onAdd,
  onRemove,
  readOnly = false,
  className,
}: PrepChecklistProps) {
  const [newItem, setNewItem] = useState('');

  const handleAdd = () => {
    if (newItem.trim() && onAdd) {
      onAdd(newItem.trim());
      setNewItem('');
    }
  };

  const completedCount = items.filter((item) => item.completed).length;
  const progress = items.length > 0 ? (completedCount / items.length) * 100 : 0;

  return (
    <div className={cn('space-y-3', className)}>
      {/* Progress */}
      {items.length > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-warm-gray">
              {completedCount} of {items.length} completed
            </span>
            <span className="font-medium text-navy">{Math.round(progress)}%</span>
          </div>
          <div className="h-2 bg-cream rounded-full overflow-hidden">
            <div
              className="h-full bg-sage rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Items */}
      <div className="space-y-2">
        {items.map((item) => (
          <div
            key={item.id}
            className={cn(
              'flex items-center gap-3 p-3 rounded-lg transition-colors',
              item.completed ? 'bg-sage/10' : 'bg-cream'
            )}
          >
            <button
              type="button"
              onClick={() => onToggle?.(item.id, !item.completed)}
              disabled={readOnly}
              className={cn(
                'flex-shrink-0 transition-colors',
                readOnly && 'cursor-default'
              )}
            >
              {item.completed ? (
                <CheckCircle className="w-5 h-5 text-sage" />
              ) : (
                <Circle className="w-5 h-5 text-warm-gray" />
              )}
            </button>
            <span
              className={cn(
                'flex-1 text-sm',
                item.completed && 'text-warm-gray line-through'
              )}
            >
              {item.item}
            </span>
            {!readOnly && onRemove && (
              <button
                type="button"
                onClick={() => onRemove(item.id)}
                className="p-1 text-warm-gray hover:text-red-500 hover:bg-red-50 rounded transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Add New */}
      {!readOnly && onAdd && (
        <div className="flex gap-2">
          <input
            type="text"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            placeholder="Add prep item..."
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAdd())}
            className="flex-1 px-3 py-2 bg-cream rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sage/20"
          />
          <button
            type="button"
            onClick={handleAdd}
            disabled={!newItem.trim()}
            className="px-3 py-2 bg-sage/10 text-sage hover:bg-sage/20 rounded-lg transition-colors disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Empty State */}
      {items.length === 0 && readOnly && (
        <p className="text-sm text-warm-gray text-center py-4">
          No prep items for this appointment
        </p>
      )}
    </div>
  );
}

export default PrepChecklist;
